import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import type {
  CreateDemandTargetRequest,
  CreateDemandTargetVersionRequest,
  DemandChangesRequest,
  DemandCombinationRequest,
  DemandSummaryRequest,
  DemandTimelineRequest,
  ListDemandTargetsRequest
} from '../Types/Requests/DemandRequests'
import type {
  AgendaDetectionResponse,
  DemandChangesResponse,
  DemandGapsResponse,
  DemandSuggestionResponse,
  DemandSummaryResponse,
  DemandTargetView,
  DemandTimelineResponse
} from '../Types/Responses/DemandResponses'

type QueryValue = string | number | boolean

/** Remove chaves vazias e transforma listas em CSV (`healthUnits=1,2`). */
function toQuery(params: object): Record<string, QueryValue> {
  const out: Record<string, QueryValue> = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value)) {
      if (value.length) out[key] = value.join(',')
      return
    }
    out[key] = value as QueryValue
  })
  return out
}

/** Tela Demanda (spec Onda 2.2 §4): leitura do summary/changes e quadro-meta. */
export default class DemandService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/demand'
  }

  async summary(params: DemandSummaryRequest): Promise<DemandSummaryResponse> {
    try {
      const { data } = await request.get<DemandSummaryResponse>(
        this.getCompletePath('/summary'),
        toQuery(params)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a demanda')
    }
  }

  async changes(params: DemandChangesRequest): Promise<DemandChangesResponse> {
    try {
      const { data } = await request.get<DemandChangesResponse>(
        this.getCompletePath('/changes'),
        toQuery({ ...params, sectorId: params.sectorId ?? 'none' })
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o que mudou')
    }
  }

  /** Timeline por setor (Onda 2.3): um dia de uma unidade. */
  async timeline(params: DemandTimelineRequest): Promise<DemandTimelineResponse> {
    try {
      const { data } = await request.get<DemandTimelineResponse>(
        this.getCompletePath('/timeline'),
        toQuery(params)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a timeline')
    }
  }

  async listTargets(params: ListDemandTargetsRequest = {}): Promise<{
    targets: DemandTargetView[]
  }> {
    try {
      const { data } = await request.get<{ targets: DemandTargetView[] }>(
        this.getCompletePath('/targets'),
        toQuery(params)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os quadros-meta')
    }
  }

  async createTarget(body: CreateDemandTargetRequest): Promise<DemandTargetView> {
    try {
      const { data } = await request.post<DemandTargetView>(this.getCompletePath('/targets'), body)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível salvar o quadro-meta')
    }
  }

  async addTargetVersion(
    targetId: number,
    body: CreateDemandTargetVersionRequest
  ): Promise<DemandTargetView> {
    try {
      const { data } = await request.post<DemandTargetView>(
        this.getCompletePath(`/targets/${targetId}/versions`),
        body
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível salvar a nova versão do quadro-meta')
    }
  }

  async suggestion(params: DemandCombinationRequest): Promise<DemandSuggestionResponse> {
    try {
      const { data } = await request.get<DemandSuggestionResponse>(
        this.getCompletePath('/targets/suggestion'),
        toQuery(params)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a sugestão do quadro-meta')
    }
  }

  async gaps(params: DemandCombinationRequest): Promise<DemandGapsResponse> {
    try {
      const { data } = await request.get<DemandGapsResponse>(
        this.getCompletePath('/gaps'),
        toQuery(params)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as vagas a criar')
    }
  }

  async agendaDetection(scheduleId: number): Promise<AgendaDetectionResponse> {
    try {
      const { data } = await request.get<AgendaDetectionResponse>(
        this.getCompletePath(`/schedules/${scheduleId}/agenda-detection`)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível detectar o tipo de agenda da escala')
    }
  }
}
